// =====================================================
// HOOK: useTasks
// Description: Hook complet pour gérer les tâches avec logging timeline automatique
// =====================================================

import { useSupabase } from './useSupabase';
import { useAuth } from './useAuth';
import { useCRUD } from './useCRUD';
import { useState, useEffect, useMemo } from 'react';
import type { Task } from '../types/task';
import type { EntityType } from '../types/timeline';
import { monitorQuery } from '../lib/supabase-monitor';
import { useUsers } from '../contexts/UsersContext';
import { getEntityDisplayName } from '../lib/timeline-logger';

export function useTasks(entityType: EntityType, entityId: string) {
  const supabase = useSupabase();
  const { user } = useAuth();
  const { usersMap } = useUsers(); // ✅ Cache des utilisateurs
  const [tasks, setTasks] = useState<Task[]>([]);
  const [enrichedTasks, setEnrichedTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  // Hook CRUD en mode manual pour avoir les fonctions update/delete sans chargement auto
  const crud = useCRUD<Task>('tasks', {
    select: '*',
    manual: true, // ✅ Ne pas charger toutes les tâches
  });

  // ✅ OPTIMISATION : Charger uniquement les tâches de l'entité spécifique
  const fetchTasks = useMemo(
    () => async () => {
      setLoading(true);
      try {
        const { data, error } = await monitorQuery(
          'tasks',
          'select',
          `${entityType}/${entityId}`,
          (() =>
            supabase
              .from('tasks')
              .select('*')
              .eq('entity_type', entityType)
              .eq('entity_id', entityId)
              .order('due_date', { ascending: true })) as any,
          'useTasks.fetchTasks'
        );

        if (!error && data) {
          setTasks(data as Task[]);
        }
      } finally {
        setLoading(false);
      }
    },
    [supabase, entityType, entityId]
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Enrichir les tâches avec les informations utilisateur depuis le cache
  useEffect(() => {
    if (tasks.length === 0) {
      setEnrichedTasks([]);
      return;
    }

    // ✅ OPTIMISATION : Utiliser le cache au lieu d'appeler RPC
    const enriched = tasks.map((task) => ({
      ...task,
      created_by_user:
        task.created_by && usersMap[task.created_by] ? usersMap[task.created_by] : null,
    }));

    setEnrichedTasks(enriched);
  }, [tasks, usersMap]);

  /**
   * Marquer une tâche comme terminée et logger dans la timeline
   */
  async function completeTask(taskId: string): Promise<boolean> {
    const task = enrichedTasks.find((t) => t.id === taskId);
    if (!task) return false;

    const success = await crud.update(taskId, {
      completed: true,
      completed_at: new Date().toISOString(),
    } as Partial<Task>);

    if (success) {
      // ✅ Récupérer le nom de l'entité pour un contexte clair
      const entityName = await getEntityDisplayName(supabase, entityType, entityId);
      const titlePrefix = entityName ? `${entityName} - ` : '';

      // Logger dans timeline
      await supabase.from('timeline_events').insert({
        entity_type: entityType,
        entity_id: entityId,
        event_type: 'task_completed',
        title: `${titlePrefix}Tâche terminée : ${task.title}`,
        description: null,
        created_by: user?.id || null,
        metadata: {
          task_id: taskId,
          completed_at: new Date().toISOString(),
          entity_name: entityName,
        },
      });
    }

    return success;
  }

  /**
   * Reporter une tâche de X jours et logger dans la timeline
   */
  async function rescheduleTask(taskId: string, daysToAdd: number): Promise<boolean> {
    const task = enrichedTasks.find((t) => t.id === taskId);
    if (!task) return false;

    const newDate = new Date(task.due_date);
    newDate.setDate(newDate.getDate() + daysToAdd);

    const success = await crud.update(taskId, {
      due_date: newDate.toISOString(),
    } as Partial<Task>);

    if (success) {
      // ✅ Récupérer le nom de l'entité pour un contexte clair
      const entityName = await getEntityDisplayName(supabase, entityType, entityId);
      const titlePrefix = entityName ? `${entityName} - ` : '';

      await supabase.from('timeline_events').insert({
        entity_type: entityType,
        entity_id: entityId,
        event_type: 'task_rescheduled',
        title: `${titlePrefix}Tâche reportée de ${daysToAdd} jours`,
        description: task.title,
        created_by: user?.id || null,
        metadata: {
          task_id: taskId,
          days_added: daysToAdd,
          new_due_date: newDate.toISOString(),
          entity_name: entityName,
        },
      });
    }

    return success;
  }

  return {
    tasks: enrichedTasks,
    pendingTasks: enrichedTasks.filter((t) => !t.completed),
    completedTasks: enrichedTasks.filter((t) => t.completed),
    loading,
    error: null,
    completeTask,
    rescheduleTask,
    refresh: fetchTasks,
  };
}
